import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  BaseOneResponseInterface,
  BulkResponseDataInterface,
} from '../../../../shared/model/interface/crud-response-interface.model';
import * as oeeAppReducer from '../../../oee.reducer';
import * as AppActions from '../../../app/actions';
import { ErrorMessageService } from '../../../../shared/service/error-message.service';
import { ILoadBalancerAllocation } from '../../../../view/settings/api-automation-settings/load-balancer-allocations/load-balancer-allocations.model';
import { LoadBalancerAllocationService } from './load-balancer-allocations.service';
import {
  ActionTypes,
  LoadBalancerAllocationsLoading,
  LoadBalancerAllocationsLoaded,
  FetchError,
  GetLoadBalancerAllocation,
  GetLoadBalancerAllocationCompleted,
  EditLoadBalancerAllocation,
  EditLoadBalancerAllocationCompleted,
  AddLoadBalancerAllocation,
  AddLoadBalancerAllocationCompleted,
  DeleteLoadBalancerAllocations,
  DeleteLoadBalancerAllocationsCompleted,
  ChannelsDataLoading,
  ChannelsDataCompleted,
} from './load-balancer-allocations.actions';
import { ApiAutomationLookupService } from '../shared/api-automation-lookup.service';
import { EApiAutomationResource } from '../shared/api-automation.model';

@Injectable()
export class LoadBalancerAllocationEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly service: LoadBalancerAllocationService,
    private readonly store: Store<oeeAppReducer.OeeAppState>,
    private readonly errorMessageService: ErrorMessageService,
    private readonly lookupService: ApiAutomationLookupService,
  ) {}

  getDataForDatatable = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.LOAD_BALANCER_ALLOCATIONS_DATA_LOADING),
      switchMap((payload: LoadBalancerAllocationsLoading) => {
        const andConditions = [];
        const searchText: string = payload.params.searchText?.trim() ?? '';
        let httpParams: HttpParams = new HttpParams()
          .append('sort', 'priority,ASC')
          .append('limit', String(payload.params.perPage || 1000))
          .append('page', String(payload.params.page));

        if (searchText) {
          andConditions.push({
            $or: [{ combinations: { $cont: searchText } }],
          });
        }

        if (andConditions.length > 0) {
          httpParams = httpParams.set('s', JSON.stringify({ $and: andConditions }));
        }

        if (payload.params.orderBy && payload.params.orderDesc !== undefined) {
          httpParams = httpParams.set('sort', `${payload.params.orderBy},${payload.params.orderDesc}`);
        }

        return this.service.getData(httpParams).pipe(
          switchMap((response) => of(new LoadBalancerAllocationsLoaded(response))),
          catchError((error) => of(new FetchError(error))),
        );
      }),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.GET_LOAD_BALANCER_ALLOCATION),
      switchMap((objectData: GetLoadBalancerAllocation) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.getOne(objectData.recordId).pipe(
          switchMap((response: BaseOneResponseInterface<ILoadBalancerAllocation>) =>
            of(new GetLoadBalancerAllocationCompleted(response), new AppActions.HideLoader()),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  addOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.ADD_LOAD_BALANCER_ALLOCATION),
      switchMap((objectData: AddLoadBalancerAllocation) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.addOne(objectData.record).pipe(
          switchMap((response: BaseOneResponseInterface<ILoadBalancerAllocation>) =>
            of(
              new AddLoadBalancerAllocationCompleted(response),
              ...(objectData.hideLoaderAfterSuccess ? [new AppActions.HideLoader()] : []),
            ),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  editOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.EDIT_LOAD_BALANCER_ALLOCATION),
      switchMap((objectData: EditLoadBalancerAllocation) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.editOne(objectData.recordId, objectData.record).pipe(
          switchMap((response: BaseOneResponseInterface<ILoadBalancerAllocation>) =>
            of(
              new EditLoadBalancerAllocationCompleted(response),
              ...(objectData.hideLoaderAfterSuccess ? [new AppActions.HideLoader()] : []),
            ),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  deleteOnes = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.DELETE_LOAD_BALANCER_ALLOCATIONS),
      switchMap((objectData: DeleteLoadBalancerAllocations) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.deleteOnes(objectData.payload).pipe(
          switchMap((response: BulkResponseDataInterface) => {
            this.errorMessageService.getTranslatedErrorMessage(response.data);

            return of(
              new DeleteLoadBalancerAllocationsCompleted(objectData.payload.length > 1, response),
              new AppActions.HideLoader(),
            );
          }),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  getChannelsData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.CHANNELS_DATA_LOADING),
      switchMap(() =>
        this.lookupService.getOptions(EApiAutomationResource.loadBalancerChannels).pipe(
          switchMap((response) => of(new ChannelsDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );
}
